import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../home-landing/components/Footer';
import Icon from '../../components/AppIcon';

const TermsOfService = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const versionNumber = '1.0';
  const lastRevision = 'January 28, 2026';

  const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms', icon: 'FileCheck' },
  { id: 'user-responsibilities', title: 'User Responsibilities', icon: 'Users' },
  { id: 'service-limitations', title: 'Service Limitations', icon: 'AlertTriangle' },
  { id: 'intellectual-property', title: 'Intellectual Property', icon: 'Copyright' },
  { id: 'liability', title: 'Liability Disclaimers', icon: 'Shield' },
  { id: 'termination', title: 'Termination', icon: 'XCircle' }];


  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value || '');
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service - Joe Graziano</title>
        <meta name="description" content="Terms of Service for Joe Graziano's portfolio website. Read our usage conditions, user responsibilities, and legal agreements." />
      </Helmet>
      <Header />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary to-secondary text-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Terms of Service
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6">
                Please read these terms and conditions carefully before using our website. By accessing this website, you agree to be bound by these terms.
              </p>
              <div className="flex flex-wrap gap-4 text-sm md:text-base">
                <div className="flex items-center gap-2">
                  <Icon name="FileText" size={18} />
                  <span>Version {versionNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={18} />
                  <span>Last Revised: {lastRevision}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Sidebar - Desktop */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Search */}
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search terms..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent" />

                  </div>
                </div>

                {/* Navigation */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Sections</h2>
                  <nav className="space-y-2">
                    {sections?.map((section) =>
                    <button
                      key={section?.id}
                      onClick={() => handleSectionClick(section?.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 flex items-center gap-2 ${
                      activeSection === section?.id ?
                      'bg-accent text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`
                      }>

                        <Icon name={section?.icon} size={16} />
                        {section?.title}
                      </button>
                    )}
                  </nav>
                </div>

                {/* Version History */}
                <div className="bg-card rounded-lg border border-border p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-2">Version History</h3>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>v{versionNumber} - {lastRevision}</div>
                    <div className="text-accent cursor-pointer hover:underline">View all versions</div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Mobile Search & Navigation */}
            <div className="lg:hidden col-span-1 space-y-4">
              {/* Search */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="relative">
                  <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent" />

                </div>
              </div>

              {/* Quick Navigation */}
              <div className="bg-card rounded-lg border border-border p-4">
                <h2 className="text-base font-semibold text-foreground mb-3">Quick Navigation</h2>
                <div className="grid grid-cols-2 gap-2">
                  {sections?.map((section) =>
                  <button
                    key={section?.id}
                    onClick={() => handleSectionClick(section?.id)}
                    className="px-3 py-2 rounded-md text-sm bg-muted hover:bg-accent hover:text-white transition-colors duration-200 text-left">

                      {section?.title}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <article className="lg:col-span-3 space-y-12">
              {/* Acceptance of Terms */}
              <section id="acceptance" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileCheck" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">1. Acceptance of Terms</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">1.1</strong> By accessing and using this website ("Service"), you accept and agree to be bound by the terms and provision of this agreement.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">1.2</strong> If you do not agree to abide by the above, please do not use this Service. These Terms of Service constitute a legally binding agreement between you and Joe Graziano.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">1.3</strong> We reserve the right to modify these terms at any time. Your continued use of the Service following any changes indicates your acceptance of the new terms.
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> We recommend reviewing these terms periodically to stay informed of any updates.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* User Responsibilities */}
              <section id="user-responsibilities" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">2. User Responsibilities</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">2.1 Acceptable Use:</strong> You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
                    </p>
                    <ul className="space-y-3 ml-6">
                      <li className="flex gap-3">
                        <Icon name="XCircle" size={18} className="text-red-500 flex-shrink-0 mt-1" />
                        <span className="text-muted-foreground">In any way that violates any applicable national or international law or regulation</span>
                      </li>
                      <li className="flex gap-3">
                        <Icon name="XCircle" size={18} className="text-red-500 flex-shrink-0 mt-1" />
                        <span className="text-muted-foreground">To transmit, or procure the sending of, any advertising or promotional material without our prior written consent</span>
                      </li>
                      <li className="flex gap-3">
                        <Icon name="XCircle" size={18} className="text-red-500 flex-shrink-0 mt-1" />
                        <span className="text-muted-foreground">To impersonate or attempt to impersonate the website owner, another user, or any other person or entity</span>
                      </li>
                      <li className="flex gap-3">
                        <Icon name="XCircle" size={18} className="text-red-500 flex-shrink-0 mt-1" />
                        <span className="text-muted-foreground">To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-card rounded-lg border border-border p-6">
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">2.2 Account Security:</strong> If you create an account on our Service, you are responsible for maintaining the security of your account and you are fully responsible for all activities that occur under the account.
                    </p>
                  </div>
                </div>
              </section>

              {/* Service Limitations */}
              <section id="service-limitations" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">3. Service Limitations</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">3.1 Availability:</strong> We do not guarantee that the Service will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Service, resulting in interruptions, delays, or errors.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">3.2 Modifications:</strong> We reserve the right to change, revise, update, suspend, discontinue, or otherwise modify the Service at any time or for any reason without notice to you.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">3.3 Content Accuracy:</strong> While we strive to provide accurate and up-to-date information, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or availability of the content on the Service.
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        <strong>Important:</strong> You acknowledge that your use of the Service is at your sole risk.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Intellectual Property */}
              <section id="intellectual-property" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Copyright" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">4. Intellectual Property Rights</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">4.1 Ownership:</strong> The Service and its original content, features, and functionality are and will remain the exclusive property of Joe Graziano and its licensors. The Service is protected by copyright, trademark, and other laws.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">4.2 Trademarks:</strong> Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Joe Graziano.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">4.3 Limited License:</strong> We grant you a limited, non-exclusive, non-transferable license to access and use the Service for personal, non-commercial purposes. This license does not include any resale or commercial use of the Service or its contents.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        <Icon name="CheckCircle" size={18} />
                        Permitted Uses
                      </h4>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• Personal viewing and browsing</li>
                        <li>• Sharing links to content</li>
                        <li>• Educational reference</li>
                      </ul>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                        <Icon name="XCircle" size={18} />
                        Prohibited Uses
                      </h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Commercial reproduction</li>
                        <li>• Unauthorized distribution</li>
                        <li>• Content scraping or copying</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Liability Disclaimers */}
              <section id="liability" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">5. Liability Disclaimers</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">5.1 Disclaimer of Warranties:</strong> THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE MAKE NO WARRANTIES, EXPRESSED OR IMPLIED, AND HEREBY DISCLAIM AND NEGATE ALL OTHER WARRANTIES INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF INTELLECTUAL PROPERTY.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">5.2 Limitation of Liability:</strong> IN NO EVENT SHALL JOE GRAZIANO, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">5.3 Indemnification:</strong> You agree to defend, indemnify, and hold harmless Joe Graziano from and against any claims, damages, obligations, losses, liabilities, costs, or debt arising from your use of and access to the Service.
                    </p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">
                        <strong>Legal Notice:</strong> Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for consequential damages. In such jurisdictions, our liability will be limited to the maximum extent permitted by law.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Termination */}
              <section id="termination" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="XCircle" size={20} className="text-accent" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">6. Termination</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <div className="bg-card rounded-lg border border-border p-6 mb-4">
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">6.1 Termination by Us:</strong> We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">6.2 Termination by You:</strong> If you wish to terminate your account, you may simply discontinue using the Service.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">6.3 Effect of Termination:</strong> Upon termination, your right to use the Service will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                    </p>
                  </div>
                </div>
              </section>

              {/* Governing Law */}
              <section className="scroll-mt-24">
                <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-lg p-8">
                  <h3 className="text-xl font-bold mb-4">Governing Law & Contact</h3>
                  <p className="mb-6">
                    These Terms shall be governed and construed in accordance with applicable laws, without regard to its conflict of law provisions.
                  </p>
                  <div className="space-y-3">
                    <p className="text-sm">
                      If you have any questions about these Terms, please contact us:
                    </p>
                    <div className="flex items-center gap-3">
                      <Icon name="Mail" size={18} />
                      <a href="mailto:legal@joegraziano.com" className="hover:underline">legal@joegraziano.ca

                      </a>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-sm text-white/80">
                      Related Policies: <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> | <Link to="/cookies" className="underline hover:text-white">Cookie Policy</Link>
                    </p>
                  </div>
                </div>
              </section>

              {/* Back to Top */}
              <div className="flex justify-center pt-8 border-t border-border">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors duration-200">

                  <Icon name="ArrowUp" size={18} />
                  Back to Top
                </button>
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </>);

};

export default TermsOfService;